// src/pages/DirectPage/useMessaging.js
import { useEffect, useState } from 'react';
import { 
  doc, 
  addDoc, 
  updateDoc, 
  collection, 
  onSnapshot, 
  orderBy, 
  query, 
  serverTimestamp, 
  getDoc 
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { getUserById } from '../../utils/userUtils';

export const useMessaging = (threadId, user) => {
  const [directs, setDirects] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);

  // Fetch other user info (could be buyer or seller)
  useEffect(() => {
    let isMounted = true;
    
    const fetchOtherUser = async () => {
      setLoading(true);
      try {
        const threadRef = doc(db, 'messages', threadId);
        const threadSnap = await getDoc(threadRef);
        
        if (threadSnap.exists()) {
          const data = threadSnap.data();
          const otherUserUid = (data.participants || []).find(uid => uid !== user.uid);
          
          if (otherUserUid && isMounted) {
            const otherUserObj = await getUserById(otherUserUid);
            
            if (isMounted) {
              setOtherUser(otherUserObj);
            }
          }
        }
      } catch (error) {
        } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (user && threadId) {
      fetchOtherUser();
    }
    
    return () => {
      isMounted = false;
    };
  }, [user, threadId]);

  // Listen for messages
  useEffect(() => {
    if (!threadId) return;

    const directsRef = collection(db, 'messages', threadId, 'direct');
    const q = query(directsRef, orderBy('createdAt', 'asc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setDirects(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsubscribe();
  }, [threadId]);

  // Send message
  const handleSend = async (e) => {
    e.preventDefault();
    if (text.trim() === '') return;

    try {
      // Ensure parent thread document exists before creating message
      const threadRef = doc(db, 'messages', threadId);
      const threadSnap = await getDoc(threadRef);
      
      if (!threadSnap.exists()) {
        alert('Unable to send message. Please try refreshing the page.');
        return;
      }

      const directsRef = collection(db, 'messages', threadId, 'direct');
      await addDoc(directsRef, {
        senderId: user.uid,
        text,
        createdAt: serverTimestamp(),
      });

      // Update parent doc with lastMessage + lastUpdated + lastMessageSender
      await updateDoc(threadRef, {
        lastMessage: text,
        lastMessageSender: user.uid, // Track who sent the last message
        lastUpdated: serverTimestamp(),
        // Only the sender has read the message initially
        // Other participants need to read it to be added to readBy
        readBy: [user.uid], // Sender has read their own message
      });

      setText('');
    } catch (error) {
      alert('Unable to send message. Please check your connection and try again.');
    }
  };

  return {
    directs,
    seller: otherUser, // Keep backward compatibility while using new variable name
    text,
    setText,
    handleSend,
    loading
  };
};